import { useState } from "react";
import { Dialog } from "@headlessui/react";
import { X, Eye, EyeOff } from "lucide-react";
import { GoogleLogin, type CredentialResponse } from "@react-oauth/google";
import { toast } from "sonner";
import { loginUser, registerUser, loginWithGoogle } from "@/api/AuthServices";
import { Link, useNavigate } from "react-router-dom";
import { useModalStore } from "@/zustand/modalStore";
import { Button } from "../ui/button";

export default function AuthModal() {
  const { isLoginOpen, closeLogin, tab, setTab } = useModalStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  console.log(error);
  const navigate = useNavigate();

  const resetError = () => setError("");

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    resetError();
    setLoading(true);

    try {
      const response = await loginUser({ email, password });

      // ✅ Defensive check
      if (response?.access && response?.refresh) {
        localStorage.setItem("accessToken", response.access);
        localStorage.setItem("refreshToken", response.refresh);
        const user = response.user || response;

        if (user?.email) localStorage.setItem("email", user.email);
        if (user?.id) localStorage.setItem("id", user.id.toString());

        toast.success("Login successful!");
        closeLogin();

        const redirectPath = sessionStorage.getItem("redirect_after_login");
        console.log("Redirect path after login:", redirectPath);

        if (redirectPath) {
          navigate("/");
          sessionStorage.removeItem("redirect_after_login");
        } else {
          navigate("/");
        }
      } else {
        // ✅ Handle non-token responses gracefully
        const message =
          response?.detail ||
          response?.message ||
          "Login failed. Please check your credentials.";
        toast.error(message);
        setError(message);
      }
    } catch (err: any) {
      console.error("Login error:", err);

      const data = err.response?.data || {};
      const errorMessage =
        data.detail ||
        data.message ||
        (Array.isArray(data.non_field_errors)
          ? data.non_field_errors.join(", ")
          : "") ||
        Object.values(data).flat().join(", ") ||
        err.message ||
        "Invalid email or password.";

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    password_confirm: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetError();
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    resetError();
    setLoading(true);

    if (formData.password !== formData.password_confirm) {
      setError("Passwords do not match");
      toast.error("Passwords do not match");
      setLoading(false);
      return;
    }

    try {

      const res = await registerUser(formData);
      toast.success(" Please verify OTP.");
      navigate("/verify-otp", {
        state: {
          email: formData.email,
          username: formData.username,
          password: formData.password,
        },
      });
      closeLogin();
    } catch (err: any) {
      const data = err.response?.data;
      let message = "Registration failed. Please try again.";

      if (typeof data === "string") message = data;
      else if (data?.email) message = data.email;
      else if (data?.username) message = data.username;
      else if (data?.non_field_errors) message = data.non_field_errors[0];

      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isLoginOpen) return null;

  return (
    <Dialog
      open={isLoginOpen}
      onClose={closeLogin}
      className="mt-20 relative z-[40]"
    >
      <div
        className="fixed inset-0 bg-slate-900/80 backdrop-blur-md"
        aria-hidden="true"
      />
      <div className="fixed inset-0 flex items-start sm:items-center justify-center p-4 pt-10 sm:pt-4 overflow-y-auto">
        <Dialog.Panel className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8 border border-slate-200 transform transition-all my-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
                FleeBy
              </h1>
              <p className="text-xs text-slate-500 tracking-wide uppercase font-medium">
                Online Shopping
              </p>
            </div>
            <button
              title="Close"
              onClick={closeLogin}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600"
            >
              <X size={20} />
            </button>
          </div>

          {/* Welcome Message */}
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-slate-800 mb-2">
              {tab === "login" ? "Welcome back!" : "Create your account"}
            </h2>
            <div className="w-12 h-1 bg-gradient-to-r from-blue-500 to-purple-600 mx-auto rounded-full"></div>
          </div>

          {/* Google Login */}
          <div className="flex justify-center">
            <div className="relative w-full max-w-sm">
              {/* Google Button */}
              <div
                className={
                  googleLoading ? "pointer-events-none opacity-60" : ""
                }
              >
                <GoogleLogin
                  onSuccess={async (credentialResponse: CredentialResponse) => {
                    const token = credentialResponse?.credential;
                    if (!token)
                      return toast.error("Google credential missing.");

                    try {
                      setGoogleLoading(true);

                      const response = await loginWithGoogle(token);
                      const user = response.user;

                      localStorage.setItem("accessToken", response.access);
                      localStorage.setItem("refreshToken", response.refresh);
                      localStorage.setItem("username", response.username);
                      localStorage.setItem("email", response.email);
                      localStorage.setItem("id", `${response.user_id}`);
                      localStorage.setItem(
                        "profilePicture",
                        user.profile_image || ""
                      );

                      toast.success("Logged in with Google!");
                      
                      const redirectPath = localStorage.getItem(
                        "redirect_after_login",
                      );

                      console.log("Redirect path after login:", redirectPath);

                      closeLogin();
                      if (redirectPath) {
                        navigate(redirectPath);
                        sessionStorage.removeItem("redirect_after_login");
                      } else {
                        navigate("/");
                      }
                    } catch {
                      toast.error("Google login failed.");
                    } finally {
                      setGoogleLoading(false);
                    }
                  }}
                  onError={() => toast.error("Google Login Failed")}
                  theme="outline"
                  size="large"
                  width="100%"
                />
              </div>

              {/* Loader Overlay */}
              {googleLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-md">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-black rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 text-slate-500 bg-white">
                or continue with
              </span>
            </div>
          </div>

          {/* Tab Switcher */}
          <div className="flex bg-slate-100 rounded-xl p-1 mb-6">
            <button
              onClick={() => setTab("login")}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${
                tab === "login"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setTab("register")}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${
                tab === "register"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Form */}

          {tab === "login" ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-4">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all bg-slate-50 focus:bg-white"
                />
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all bg-slate-50 focus:bg-white"
                  />
                  <Button
                    title={showPassword ? "Hide Password" : "Show Password"}
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:bg-blue-800 text-white rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Logging in...
                  </span>
                ) : (
                  "Login"
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    closeLogin();
                    navigate("/reset-password");
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-4">
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email address"
                  required
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all bg-slate-50 focus:bg-white"
                />
                <input
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Full name"
                  required
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all bg-slate-50 focus:bg-white"
                />
                <div className="relative">
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create password"
                    minLength={8}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all bg-slate-50 focus:bg-white"
                  />
                  <Button
                    title={showPassword ? "Hide Password" : "Show Password"}
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <div className="relative">
                  <input
                    name="password_confirm"
                    type={showPassword ? "text" : "password"}
                    value={formData.password_confirm}
                    onChange={handleChange}
                    placeholder="Confirm password"
                    minLength={8}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all bg-slate-50 focus:bg-white"
                  />
                  <Button
                    title={showPassword ? "Hide Password" : "Show Password"}
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:bg-blue-800 text-white rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  "Create Account"
                )}
              </button>
            </form>
          )}

          {/* Divider */}

          {/* Terms */}
          <p className="text-xs text-slate-500 text-center mt-6 leading-relaxed">
            By continuing, you agree to FleeBy's{" "}
            <Link to="/terms" className="text-blue-600 hover:underline">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/privacy-policy"
              className="text-blue-600 hover:underline"
            >
              Privacy Policy
            </Link>
          </p>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
